import { useEffect, useRef } from 'react';

import {
  getMixpanelSessionId,
  trackSpotlightPageExited,
  trackSpotlightPageViewed,
} from '@/lib/mixpanel/mixpanel';
import { useCurrentUser } from '@/user/use-current-user.query';
import { useUserRole } from '@/user/use-user-role';

const SPOTLIGHT_EXIT_DEFER_MS = 100;

export const useSpotlightPageTracking = (): void => {
  const { data: user } = useCurrentUser();
  const userRole = useUserRole();
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTrackedViewRef = useRef(false);
  const pageEnteredAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (user === undefined || user === null || userRole === null) {
      return;
    }

    if (exitTimeoutRef.current !== null) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }

    if (!hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      pageEnteredAtRef.current = Date.now();
      trackSpotlightPageViewed({
        session_id: getMixpanelSessionId(),
        user_id: user.id,
        user_role: userRole,
      });
    }

    const userId = user.id;

    return () => {
      const enteredAt = pageEnteredAtRef.current;
      if (enteredAt === null) {
        return;
      }

      exitTimeoutRef.current = setTimeout(() => {
        exitTimeoutRef.current = null;
        hasTrackedViewRef.current = false;
        pageEnteredAtRef.current = null;
        trackSpotlightPageExited({
          session_id: getMixpanelSessionId(),
          time_on_page_seconds: Math.round((Date.now() - enteredAt) / 1000),
          user_id: userId,
        });
      }, SPOTLIGHT_EXIT_DEFER_MS);
    };
  }, [user, userRole]);
};
